/**
 * Thread side panel — the parent message, its replies, and a reply composer.
 *
 * Moved out of MessagingWorkspace.tsx so replies render like the main thread:
 * safe links + link previews (MessageText / MessageLinkPreviews), attachments
 * (AttachmentDisplay), and a paperclip on the reply composer that reuses the
 * same upload endpoint as the main composer. The reply composer has the same
 * Attach menu (computer, Google Drive, OneDrive & SharePoint) and Insert-link
 * control as the main composer.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, FileText, Loader2, AlertTriangle } from 'lucide-react';
import { messagingApi, Message, UploadedAttachment } from '@/services/messagingApi';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '../ui/button';
import { AutoGrowTextarea } from './AutoGrowTextarea';
import { InlineNotice, useInlineNotice } from './InlineNotice';
import { useSupportBubbleClearance } from '@/hooks/useSupportBubbleClearance';
import { useStickToBottom } from '@/hooks/useStickToBottom';
import { AttachmentDisplay } from './AttachmentActions';
import { MessageText, visibleMessageText } from './linkify';
import { MessageLinkPreviews } from './LinkPreviewCard';
import { AttachMenu } from './AttachMenu';
import { InsertLinkControl, useComposerLink } from './InsertLink';
import { ProviderMark } from './cloud/CloudIcons';
import { getCurrentUserId } from '../../utils/auth';
import { MentionPicker, useMentionComposer } from './mentions';
import { callMetaOf, callNotesMetaOf } from '@/services/callsApi';
import { CallMessageCard } from './calls/CallMessageCard';
import { CallNotesCard } from './calls/CallNotesCard';
import { systemEventMetaOf } from '@/services/systemEventsApi';
import { SystemEventCard, SystemEventSender } from './systemEvents/SystemEventCard';

/** Red "Urgent" tag for urgent messages (matches the main thread). */
function UrgentTag({ message }: { message: Message }) {
  const { t } = useAppTranslation();
  if (message.priority !== 'urgent') return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-red-600 px-1.5 py-0 text-[10px] font-semibold text-white"
      data-testid="urgent-label"
    >
      <AlertTriangle className="h-3 w-3" />
      {t('messaging.urgent.label', 'Urgent')}
    </span>
  );
}

/** The sender line: "WeaveIn" + Bot badge for a system-event post (TRUS-684). */
function SenderName({ message }: { message: Message }) {
  if (systemEventMetaOf(message.metadata)) return <SystemEventSender />;
  return <span className="font-semibold text-sm">{message.sender.name}</span>;
}

interface ThreadPanelProps {
  message: Message;
  channelId: number;
  /** Offer members in the @ picker (false for member-portal contexts). Default true. */
  allowMemberMentions?: boolean;
}

function MessageBody({ message }: { message: Message }) {
  const isOwn = message.sender.user_id != null && message.sender.user_id === getCurrentUserId();
  const callMeta = callMetaOf(message.metadata);
  const notesMeta = callNotesMetaOf(message.metadata);
  const systemEvent = systemEventMetaOf(message.metadata);
  return (
    <>
      {systemEvent ? (
        <SystemEventCard meta={systemEvent} />
      ) : callMeta ? (
        <CallMessageCard meta={callMeta} channelId={message.channel_id} />
      ) : notesMeta ? (
        <CallNotesCard meta={notesMeta} />
      ) : visibleMessageText(message.content, !!message.attachments?.length) && (
        <div className="text-sm whitespace-pre-wrap break-words">
          <MessageText content={message.content} />
        </div>
      )}
      {!systemEvent && (
        <MessageLinkPreviews content={message.content} metadata={message.metadata} suppressed={message.suppress_link_previews} />
      )}
      {message.has_attachments && message.attachments?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {message.attachments.map((attachment) => (
            <AttachmentDisplay
              key={attachment.id}
              attachment={attachment}
              channelId={message.channel_id}
              compact={message.attachments.length > 2}
              isOwn={isOwn}
            />
          ))}
        </div>
      )}
    </>
  );
}

export function ThreadPanel({ message, channelId, allowMemberMentions = true }: ThreadPanelProps) {
  const { t } = useAppTranslation();
  const { notice, show: showNotice, clear: clearNotice } = useInlineNotice();
  const [replyInput, setReplyInput] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [cloudAttachments, setCloudAttachments] = useState<UploadedAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const composerLink = useComposerLink(replyInputRef, replyInput, setReplyInput);
  const mentions = useMentionComposer({
    inputRef: replyInputRef,
    setValue: setReplyInput,
    channelId,
    includeMembers: allowMemberMentions,
  });
  const { fmt } = useFormattedDate();
  const queryClient = useQueryClient();

  const { data: thread, isLoading } = useQuery({
    queryKey: ['thread', message.id],
    queryFn: () => messagingApi.getThread(message.id),
  });

  // Keep the floating support launcher off the Reply button.
  const replyRowRef = useRef<HTMLDivElement>(null);
  useSupportBubbleClearance(replyRowRef, !isLoading);

  // Open on the newest reply, and follow new ones (scrolls the list only).
  const repliesRef = useRef<HTMLDivElement>(null);
  const replyCount = thread?.replies?.length ?? 0;
  useEffect(() => {
    const el = repliesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [replyCount, isLoading]);

  // Stay on the newest reply while the list's height changes (the reply box
  // growing as you type, chips appearing), unless the user scrolled up.
  useStickToBottom(repliesRef, !isLoading);

  const sendReplyMutation = useMutation({
    mutationFn: (payload: { content: string; attachmentIds: number[] }) =>
      messagingApi.sendMessage({
        channel_id: channelId,
        content: payload.content,
        thread_id: message.id,
        attachments: payload.attachmentIds.length > 0 ? payload.attachmentIds.map((id) => ({ id })) : undefined,
      }),
    onSuccess: () => {
      setReplyInput('');
      setFiles([]);
      setCloudAttachments([]);
      queryClient.invalidateQueries({ queryKey: ['thread', message.id] });
      queryClient.invalidateQueries({ queryKey: ['channel-messages', channelId] });
    },
    onError: (error: Error) => {
      showNotice({
        kind: 'error',
        title: t('messaging.composer.sendFailed', 'Message not sent'),
        description: error?.message,
      });
    },
  });

  const handleSendReply = async () => {
    if (!replyInput.trim() && files.length === 0 && cloudAttachments.length === 0) return;
    setIsUploading(true);
    const attachmentIds: number[] = cloudAttachments.map((a) => a.id);
    try {
      for (const file of files) {
        try {
          const result = await messagingApi.uploadAttachment(channelId, file);
          attachmentIds.push(result.id);
        } catch (error) {
          showNotice({
            kind: 'error',
            title: t('messaging.composer.uploadFailed', 'Could not attach {{name}}', { name: file.name }),
            description: (error as Error)?.message,
          });
        }
      }
    } finally {
      setIsUploading(false);
    }
    const content = replyInput.trim() ? replyInput : attachmentIds.length > 0 ? '[Attachment]' : '';
    if (!content) return;
    sendReplyMutation.mutate({ content, attachmentIds });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const busy = isUploading || sendReplyMutation.isPending;

  return (
    // Fills what is left under the panel's own title bar: the replies list is
    // the only part that scrolls and the reply box stays on screen.
    <div className="flex h-full min-h-0 flex-1 flex-col" data-composer-pane>
      {/* Parent Message */}
      <div className="max-h-[35%] flex-shrink-0 overflow-y-auto p-4 border-b bg-slate-50 dark:bg-slate-900">
        <div className="flex items-center gap-2 mb-2">
          <SenderName message={message} />
          <span className="text-xs text-muted-foreground">{fmt.dateTime(message.created_at)}</span>
          <UrgentTag message={message} />
        </div>
        <MessageBody message={message} />
      </div>

      {/* Replies */}
      <div ref={repliesRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4" data-testid="thread-replies">
        {thread?.replies.map((reply) => (
          <div key={reply.id} className="flex gap-3">
            <div className="w-8 h-8 flex-shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
              {reply.sender.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2 mb-1">
                <SenderName message={reply} />
                <span className="text-xs text-muted-foreground">{fmt.dateTime(reply.created_at)}</span>
                <UrgentTag message={reply} />
              </div>
              <MessageBody message={reply} />
            </div>
          </div>
        ))}
        {(!thread?.replies || thread.replies.length === 0) && (
          <div className="text-center text-muted-foreground text-sm py-8">
            {t('messaging.thread.noReplies', 'No replies yet. Start the conversation!')}
          </div>
        )}
      </div>

      {/* Draft extras, capped so they scroll instead of pushing the reply box off screen. */}
      <div className="max-h-[40%] min-h-0 flex-shrink-0 overflow-y-auto">
      {/* Pending reply attachments */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t bg-slate-50 px-4 py-2 dark:bg-slate-900">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 rounded border bg-white px-2 py-1 text-sm dark:bg-slate-800"
            >
              <FileText className="h-4 w-4" />
              <span className="max-w-[140px] truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                className="text-muted-foreground hover:text-destructive"
                title={t('messaging.channel.remove', 'Remove')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {cloudAttachments.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t bg-slate-50 px-4 py-2 dark:bg-slate-900">
          {cloudAttachments.map((att) => (
            <div
              key={att.id}
              className="flex items-center gap-2 rounded border bg-white px-2 py-1 text-sm dark:bg-slate-800"
            >
              <ProviderMark provider={att.provider || 'google_drive'} />
              <span className="max-w-[140px] truncate">{att.filename}</span>
              <button
                type="button"
                onClick={() => setCloudAttachments((prev) => prev.filter((a) => a.id !== att.id))}
                className="text-muted-foreground hover:text-destructive"
                title={t('messaging.channel.remove', 'Remove')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {notice && (
        <div className="border-t px-4 py-2">
          <InlineNotice notice={notice} onClose={clearNotice} />
        </div>
      )}
      </div>

      {/* Reply Input */}
      <div ref={replyRowRef} className="flex-shrink-0 p-4 border-t" data-testid="thread-reply-composer">
        {/* items-end: the buttons stay on the last line as the reply box grows. */}
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const picked = Array.from(e.target.files || []);
              if (picked.length > 0) setFiles((prev) => [...prev, ...picked]);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.tar,.gz"
          />
          <AttachMenu
            channelId={channelId}
            disabled={busy}
            onUploadClick={() => fileInputRef.current?.click()}
            onCloudAttached={(att) =>
              setCloudAttachments((prev) => (prev.some((a) => a.id === att.id) ? prev : [...prev, att]))
            }
            onError={({ title, description }) => showNotice({ kind: 'error', title, description })}
          />
          <InsertLinkControl link={composerLink} disabled={busy} />
          <div className="relative min-w-0 flex-1">
            <AutoGrowTextarea
              ref={replyInputRef}
              value={replyInput}
              onChange={(e) => {
                setReplyInput(e.target.value);
                mentions.handleChange(e);
              }}
              placeholder={t('messaging.thread.replyPlaceholder', 'Reply...')}
              aria-label={t('messaging.thread.replyPlaceholder', 'Reply...')}
              onPaste={composerLink.inputProps.onPaste}
              data-claims-shortcuts={composerLink.inputProps['data-claims-shortcuts']}
              onKeyDown={(e) => {
                if (composerLink.inputProps.onKeyDown(e)) return;
                mentions.handleKeyDown(e);
              }}
              onSubmit={handleSendReply}
            />
            <MentionPicker composer={mentions} />
          </div>
          <Button onClick={handleSendReply} disabled={busy || (!replyInput.trim() && files.length === 0 && cloudAttachments.length === 0)}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('messaging.thread.reply', 'Reply')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ThreadPanel;
